import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('website_alt') as string || '').trim();

    if (honeypotVal) {
      setFormStatus('success');
      return;
    }

    formData.delete('website_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u3k8l3pcjqs2fcslc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const code = (parsed?.code as string) || '';
      const serverMsg = ((parsed?.meta as Record<string, unknown>)?.message as string) || (parsed?.message as string) || responseText || '';

      if (res.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || t('about.contactFormError'));
      }
    } catch {
      setFormStatus('error');
      setFormError(t('about.contactFormError'));
    }
  };

  const breadcrumbItems = [
    { label: t('common.breadcrumbHome'), href: '/' },
    { label: t('about.title') },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '日韩在线永久域名',
    url: 'https://zhihaiyunxin.cn',
    description: '日韩在线永久域名是国内领先的日韩在线内容域名收录平台，成立于2020年，已服务超过50万用户。',
    foundingDate: '2020',
    email: 'contact@rihan-domain.com',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CN',
      addressLocality: '广州市',
      addressRegion: '广东省',
      streetAddress: '体育西路111号建和中心',
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 日韩在线永久域名',
    description: '了解日韩在线永久域名的品牌故事、信誉和联系方式。',
    url: 'https://zhihaiyunxin.cn/about',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <main className="pt-20 md:pt-24">
        <Breadcrumb items={breadcrumbItems} />

        {/* Page Header */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading">
              {t('about.title')}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              了解日韩在线永久域名的品牌故事、发展历程与核心团队
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div className="flex flex-col justify-center">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4 font-heading">
                  {t('about.brandIntro')}
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {t('about.brandIntroContent')}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  经过六年的发展，我们已经成为中文互联网上最值得信赖的日韩在线域名收录平台之一。平台累计收录域名超过100个，内容涵盖日韩视频、动漫、综艺、音乐、资讯等多个领域。
                </p>
              </div>
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/60">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: t('about.stats1Label'), value: t('about.stats1Value') },
                    { label: t('about.stats2Label'), value: t('about.stats2Value') },
                    { label: t('about.stats3Label'), value: t('about.stats3Value') },
                    { label: t('about.stats4Label'), value: t('about.stats4Value') },
                  ].map((stat, i) => (
                    <div key={i} className="text-center p-4 rounded-xl bg-background-100">
                      <div className="text-2xl md:text-3xl font-bold text-primary-600 font-heading mb-1">{stat.value}</div>
                      <div className="text-xs text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 mb-4">
                  <i className="ri-focus-3-line text-primary-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3 font-heading">
                  {t('about.brandMission')}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t('about.brandMissionContent')}
                </p>
              </div>
              <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mb-4">
                  <i className="ri-eye-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3 font-heading">
                  {t('about.brandVision')}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t('about.brandVisionContent')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 font-heading text-center">
              {t('about.reputation')}
            </h2>
            <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/60">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                {t('about.reputationContent')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                {[
                  { icon: 'ri-shield-check-line', title: '人工审核', desc: '所有域名均经人工审核，确保安全可靠' },
                  { icon: 'ri-timer-flash-line', title: '7×24监控', desc: '全天候监控域名状态，第一时间更新' },
                  { icon: 'ri-user-star-line', title: '用户至上', desc: '50万+用户的信赖之选，口碑见证' },
                ].map((item, i) => (
                  <div key={i} className="text-center p-4">
                    <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className={`${item.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1 font-heading">{item.title}</h4>
                    <p className="text-xs text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 font-heading text-center">
              {t('about.teamIntro')}
            </h2>
            <div className="bg-background-100 rounded-2xl p-6 md:p-10 border border-background-200/60">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {t('about.teamIntroContent')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                {[
                  { icon: 'ri-code-s-slash-line', role: '技术开发组', desc: '负责网站开发维护和域名监控系统建设' },
                  { icon: 'ri-edit-line', role: '内容运营组', desc: '负责域名信息采集、审核和文章撰写' },
                  { icon: 'ri-radar-line', role: '域名监控组', desc: '7×24小时轮班监控域名状态，确保及时更新' },
                ].map((item, i) => (
                  <div key={i} className="text-center p-4">
                    <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-lg bg-secondary-100">
                      <i className={`${item.icon} text-secondary-600 text-lg`}></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-950 mb-1 font-heading">{item.role}</h4>
                    <p className="text-xs text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2 font-heading text-center">
              {t('about.contactTitle')}
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              {t('about.contactSubtitle')}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* Contact Info */}
              <div className="lg:col-span-2 flex flex-col gap-4">
                {[
                  { icon: 'ri-mail-line', label: t('about.contactEmail'), value: 'contact@rihan-domain.com' },
                  { icon: 'ri-wechat-line', label: t('about.contactWechat'), value: 'rihan_domain_official' },
                  { icon: 'ri-qq-line', label: t('about.contactQQ'), value: '4001234567' },
                  { icon: 'ri-map-pin-line', label: t('about.address'), value: '广东省广州市天河区体育西路111号建和中心' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 bg-background-50 rounded-xl p-4 border border-background-200/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className={`${item.icon} text-primary-600`}></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400 mb-0.5">{item.label}</div>
                      <div className="text-sm font-medium text-foreground-800">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-3 bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/60">
                <h3 className="text-lg font-semibold text-foreground-950 mb-6 font-heading">
                  {t('about.contactForm')}
                </h3>

                {formStatus === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                      <i className="ri-check-line text-accent-600 text-2xl"></i>
                    </div>
                    <p className="text-foreground-700 font-medium">{t('about.contactFormSuccess')}</p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                    >
                      继续留言
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t('about.contactFormName')}
                        </label>
                        <input
                          type="text"
                          id="contact_name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                          placeholder="请输入您的称呼"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t('about.contactFormEmail')}
                        </label>
                        <input
                          type="email"
                          id="contact_email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                          placeholder="请输入您的电子邮箱"
                        />
                      </div>
                    </div>

                    <div className="website_alt_wrapper">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {t('about.contactFormMessage')}
                      </label>
                      <textarea
                        id="contact_message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>

                    {formStatus === 'error' && (
                      <p className="text-sm text-red-600 mb-4">{formError}</p>
                    )}

                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {t('about.contactFormSubmit')}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}